import { Component, Input, ElementRef, AfterViewChecked, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { OOTSRequestSession } from 'src/app/_model/oots-request-session';
import { SessionService } from 'src/app/_service/session.service';

export enum SessionStep {
  PREVIOUS_SESSION = 'PREVIOUS_SESSION', // paso 1 → elegir nueva o continuar
  TOKEN = 'TOKEN',                       // paso 2 → introducir token de referencia
  EMAIL = 'EMAIL',                       // pedir email
  CODE = 'CODE',                         // introducir código enviado al email
}

@Component({
  selector: 'app-session-management',
  templateUrl: './session-management.component.html',
  styleUrls: ['./session-management.component.css']
})
export class SessionManagementComponent implements AfterViewChecked {

  @Input() modalOpened: boolean = false;
  @Input() session!: OOTSRequestSession;
  @ViewChild('continueBtn', { static: false }) continueBtn!: ElementRef<HTMLButtonElement>;

  step: SessionStep = SessionStep.PREVIOUS_SESSION;
  SessionStep = SessionStep;

  @ViewChild('input0') input0!: ElementRef;
  @ViewChild('input1') input1!: ElementRef;
  @ViewChild('input2') input2!: ElementRef;
  @ViewChild('input3') input3!: ElementRef;
  
  codeInputs: ElementRef[] = [];
  code: string[] = Array(4).fill('');
  hasFocusedInput = false;

  email: string = '';
  errorMessage: string | null = null;
  lang: string | null = null;
  result: ActionResult;

  constructor(
    private sessionService: SessionService,
    private router: Router,
    public translate: TranslateService
  ) {
    this.lang = window.localStorage.getItem('lang');
    if (this.lang == null) this.lang = "es";
  }

  private showModalSubject = new Subject<boolean>();
  showModal$ = this.showModalSubject.asObservable();

  private modalResultSubject = new Subject<any>();
  modalResult$ = this.modalResultSubject.asObservable();

  openSessionModal() {
    this.showModalSubject.next(true);
  }

  closeSessionModal() {
    this.showModalSubject.next(false);
  }

  emitirResult(data: any) {
    this.modalResultSubject.next(data);
    this.closeSessionModal();
  }

  ngAfterViewChecked(): void {
    if (!this.hasFocusedInput && this.input0) {
      this.input0.nativeElement.focus();
      this.hasFocusedInput = true;
    }
  }

  handlePaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedText: string = event.clipboardData?.getData('text') ?? '';
    this.codeInputs = [this.input0, this.input1, this.input2, this.input3];

    for (let i = 0; i < this.codeInputs.length; i++) {
      const char = pastedText[i] ?? '';
      this.code[i] = char;
      this.codeInputs[i].nativeElement.value = char;
    }

    if (pastedText.length >= this.codeInputs.length) {
      setTimeout(() => {
        this.continueBtn.nativeElement.focus();
      }, 100);
    }
  }


  /** PREVIOUS_SESSION → Nueva sesión */
  startNewSession() {
    this.session.uuid = this.session.currentSession;
    this.session.action = SessionAction.NEW;
    this.sessionService.selectSession(this.session).subscribe({
      next: () => {
        this.router.navigate(['/home'], { queryParams: { uuid: this.session.currentSession } });
        this.modalOpened = false;
      },
      error: (e) => console.error(e),
    });
  }

  /** TOKEN → Continuar con referencia */
  continueWithToken(reference: string) {
    this.session.uuid = this.session.previousSession;
    this.session.referenceToken = reference;
     this.session.action = SessionAction.CONTINUE;
    this.sessionService.selectSession(this.session).subscribe({
      next: () => {
        this.router.navigate(['/home'], { queryParams: { uuid: this.session.previousSession } });
        this.modalOpened = false;
      },
      error: (e) => {
        this.errorMessage = this.translate.instant('SESSION_MANAGEMENT.ERROR_INVALID_REFERENCE');
        console.error(e);
        }
    });
  }

  /** EMAIL → Enviar código a email */
  sendSecurityCode() {
    if (!this.isValidEmail(this.email)) {
      this.errorMessage = this.translate.instant('SESSION_MANAGEMENT.ERROR_INVALID_EMAIL');
      return;
    }

    this.sessionService.getSecurityCode(this.session.previousSession, "FINISH", this.email, this.lang!).subscribe(
      {
        next: response => {
          this.errorMessage = null;
          this.step = SessionStep.CODE;
        },
        error: (error) => {
          this.errorMessage = this.translate.instant('SESSION_MANAGEMENT.ERROR_SENDING_CODE');
        }
      });
  }

  /** CODE → Validar código */
  validateSecurityCode() {
    const fullCode = [this.input0, this.input1, this.input2, this.input3].map(input => input.nativeElement.value).join('');
    const uuid = this.session.uuid ?? this.session.previousSession;
    const lang = this.lang ?? 'es';

    this.sessionService.validateSecurityCode(uuid, fullCode, lang).subscribe({
      next: (response: string) => {
        const result: { success: boolean } = JSON.parse(response);
        if (result.success) this.moveToStep(SessionStep.TOKEN);
        else this.errorMessage = this.translate.instant('SESSION_MANAGEMENT.ERROR_INVALID_CODE');
      },
      error: (err) => {
        console.error('Error validando el código de seguridad', err);
        this.errorMessage = this.translate.instant('SESSION_MANAGEMENT.ERROR_INVALID_CODE');
      }
    });
  }

  /** RESULT → Acción final */
  finishSession() {
    this.router.navigate(['/home'], { queryParams: { uuid: this.session.previousSession } });
    this.modalOpened = false;
  }

  /** helpers */
  isValidEmail(email: string) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@.]+$/;
    return email && regex.test(email);
  }

  moveNext(index: number, event: any): void {
    this.errorMessage = null;
    this.code[index] = event.target.value;
    if (event.target.value && index < 3) {
      this.focusNextInput(index + 1);
    }
  }

   focusNextInput(index: number): void {
    switch (index) {
      case 1:
        this.input1.nativeElement.focus();
        break;
      case 2:
        this.input2.nativeElement.focus();
        break;
      case 3:
        this.input3.nativeElement.focus();
        break;
      default:
        break;
    }
  }

  moveToStep(sessionStep: SessionStep ) {
    this.errorMessage = null;
    this.step = sessionStep;

  }

  obtenerReferencia(event: Event) {
    event.preventDefault();
    this.moveToStep(SessionStep.EMAIL);
  }

  resetMessage() {
    this.errorMessage = null;
  }

}

export enum SessionAction {
  CONTINUE = "CONTINUE",
  NEW = "NEW"
}

export interface ActionResult {
  message: string,
}
